import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, Clock, CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsultationPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    travel_interests: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user cancelled payment
    if (searchParams.get('cancelled') === 'true') {
      toast.error('Payment was cancelled. Please try again when ready.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Create checkout session for $25 consultation fee
      const response = await axios.post(`${API}/consultations/checkout`, {
        ...formData,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create payment session. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to process request. Please try again.');
      setLoading(false);
    }
  };

  const benefits = [
    'Personalized travel recommendations based on your preferences',
    'Access to exclusive deals not available online',
    'Expert guidance on destinations, resorts, and cruises',
    'Complete itinerary planning and booking assistance',
    'Travel document and visa guidance',
    'Support before, during, and after your trip'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-navy py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-gold font-medium tracking-widest text-sm uppercase">Get Started</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mt-4 mb-6">
            Book Your Travel Consultation
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-lg mb-6">
            Schedule a personalized consultation to discuss your dream vacation with Brandi.
          </p>
          <div className="inline-flex items-center gap-4 bg-white/10 rounded-full px-6 py-3">
            <CreditCard className="w-6 h-6 text-gold" />
            <span className="text-white font-medium">
              <span className="text-gold text-2xl font-serif font-bold">$25</span>
              {' '}consultation fee — credited toward your booking
            </span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Benefits */}
            <div>
              <h2 className="font-serif text-3xl text-navy mb-6">
                What You'll Get
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-0.5" />
                    <p className="text-text-secondary">{benefit}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 bg-cream rounded-2xl p-6">
                <h3 className="font-serif text-xl text-navy mb-4">How It Works</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-navy font-bold">1</div>
                    <div>
                      <p className="font-semibold text-navy">Submit Your Request</p>
                      <p className="text-sm text-text-muted">Fill out the form with your travel interests</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-navy font-bold">2</div>
                    <div>
                      <p className="font-semibold text-navy">Pay Consultation Fee</p>
                      <p className="text-sm text-text-muted">$25 fee is credited toward your booking</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-navy font-bold">3</div>
                    <div>
                      <p className="font-semibold text-navy">Schedule Your Call</p>
                      <p className="text-sm text-text-muted">Brandi will contact you within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-navy font-bold">4</div>
                    <div>
                      <p className="font-semibold text-navy">Plan Your Dream Trip</p>
                      <p className="text-sm text-text-muted">Get personalized recommendations and booking assistance</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Consultation Form */}
            <div>
              <form onSubmit={handleSubmit} className="bg-cream rounded-3xl p-8 shadow-card">
                <h2 className="font-serif text-2xl text-navy mb-6 text-center">
                  Request Your Consultation
                </h2>
                
                <div className="space-y-5">
                  <div>
                    <Label className="text-navy font-medium">Full Name *</Label>
                    <Input 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <Label className="text-navy font-medium">Email Address *</Label>
                    <Input 
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <Label className="text-navy font-medium">Phone Number</Label>
                    <Input 
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-navy font-medium">Preferred Date</Label>
                      <Input 
                        type="date"
                        value={formData.preferred_date}
                        onChange={(e) => setFormData({...formData, preferred_date: e.target.value})}
                        className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                      />
                    </div>
                    <div>
                      <Label className="text-navy font-medium">Preferred Time</Label>
                      <select
                        value={formData.preferred_time}
                        onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
                        className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-2 focus:border-gold focus:ring-gold"
                      >
                        <option value="">Select time</option>
                        <option value="morning">Morning (9am-12pm)</option>
                        <option value="afternoon">Afternoon (12pm-5pm)</option>
                        <option value="evening">Evening (5pm-8pm)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-navy font-medium">Travel Interests *</Label>
                    <Textarea 
                      required
                      value={formData.travel_interests}
                      onChange={(e) => setFormData({...formData, travel_interests: e.target.value})}
                      className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                      placeholder="Tell us about your dream vacation - destinations, type of trip (resort, cruise, adventure), dates, budget range..."
                      rows={4}
                    />
                  </div>

                  <div>
                    <Label className="text-navy font-medium">Additional Notes</Label>
                    <Textarea 
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      className="mt-2 rounded-xl border-gray-200 focus:border-gold focus:ring-gold"
                      placeholder="Any special requests or questions?"
                      rows={2}
                    />
                  </div>
                </div>

                <div className="mt-8">
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gold hover:bg-gold-dark text-navy font-semibold py-6 rounded-full text-lg shadow-luxury"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-navy border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Calendar className="w-5 h-5 mr-2" />
                        Submit & Pay $25
                      </>
                    )}
                  </Button>
                  <p className="text-center text-sm text-text-muted mt-4">
                    The $25 consultation fee will be credited toward any vacation booked through our agency.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConsultationPage;
